@if (contacts.callTarget(); as person) {
  <button type="button" data-alert-backdrop tabindex="-1" [attr.aria-label]="t.cancel" (click)="contacts.cancelCall()" (keydown.escape)="contacts.cancelCall()"></button>
  <div #callDialog data-alert role="alertdialog" aria-modal="true" [attr.aria-labelledby]="t.callAlertTitle" [attr.aria-describedby]="t.callAlertDetail">
    <span data-alert-icon aria-hidden="true">
      <svg ui-icon name="phone"></svg>
    </span>
    <h2 id="call-alert-title">Call {{ person.name }}?</h2>
    <p id="call-alert-detail">{{ person.detail.split(' · ').slice(0, 2).join(' · ') }}</p>
    @if (person.context) {
      <p data-system>{{ person.context }}</p>
    }
    <div data-alert-actions>
      <button type="button" (click)="contacts.cancelCall()">{{ t.cancel }}</button>
      <button type="button" data-primary (click)="confirmCall()">{{ t.call }}</button>
    </div>
  </div>
}

<aside
  ui-sheet
  [attr.aria-label]="contacts.emailOpen() ? t.emailSheetAriaLabel : t.textSheetAriaLabel"
  [open]="sheetOpen()"
  (openChange)="onSheetOpenChange($event)"
  [expandable]="remote && !contacts.emailOpen()"
  [expanded]="expanded() && !contacts.emailOpen()"
  (expandedChange)="onExpandedChange($event)"
  [heading]="contacts.emailOpen() ? contacts.emailTarget().name : (remote ? remoteHeading() : contacts.active().name)"
  [subheading]="contacts.emailOpen() ? t.emailSubheading : (remote ? remoteSubheading() : contacts.active().detail)"
  (dragover)="onDragOver($event)"
  (dragleave)="onDragLeave($event)"
  (drop)="onDrop($event)">
<div data-sheet-top-bar>
  <div data-sheet-nav>
    <div ui-segmented [options]="sheetPanelOptions" [value]="contacts.emailOpen() ? 'email' : contacts.activePanel()" (valueChange)="selectSheetPanel($any($event))" [attr.ariaLabel]="t.panelLabel"></div>
  </div>
  <div data-away-bar>
    <button ui-button variant="secondary" type="button" data-away-mode [attr.aria-pressed]="state.awayMode()" [attr.aria-label]="state.awayModeAria()" (click)="state.toggleAwayMode()">{{ state.awayModeLabel() }}<i data-away-dot aria-hidden="true"></i></button>
  </div>
</div>
@if (contacts.emailOpen() || contacts.activePanel() === 'email') {
  <form data-email-composer (submit)="$event.preventDefault(); submitEmail()">
    @if (!contacts.emailEligibilityState().allowed) {
      <p data-email-banner data-tone="blocked" role="alert">
        {{ contacts.emailEligibilityState().reason }}
      </p>
    }
    @if (contacts.emailStatus() === 'sent') {
      <p data-email-banner data-tone="sent" role="status">
        {{ contacts.emailStatusMessage() }}
      </p>
    } @else if (contacts.emailStatus() === 'blocked' || contacts.emailStatus() === 'error') {
      <p data-email-banner data-tone="blocked" role="alert">
        {{ contacts.emailStatusMessage() }}
      </p>
    }

    <label data-email-field>
      <span>{{ t.fromLabel }}</span>
      <input type="text" [value]="contacts.senderMailboxLabel()" readOnly data-readonly />
    </label>

    <label data-email-field>
      <span>{{ t.toLabel }}</span>
      <div data-email-token-box [attr.data-invalid]="contacts.emailRecipientError() ? '' : null">
        @for (addr of contacts.emailRecipients(); track addr) {
          <span data-recipient-chip>
            <span>{{ addr }}</span>
            <button
              type="button"
              [attr.aria-label]="t.removeRecipientLabel + ' ' + addr"
              (click)="$event.stopPropagation(); contacts.removeEmailRecipient(addr)">{{ t.removeChar }}</button>
          </span>
        }
        <input
          type="text"
          name="recipient"
          [attr.aria-label]="t.toLabel"
          [attr.aria-invalid]="contacts.emailRecipientError() ? 'true' : null"
          [attr.placeholder]="contacts.emailRecipients().length === 0 ? t.addRecipientPlaceholder : ''"
          [value]="contacts.emailRecipientDraft()"
          (input)="onEmailRecipientInput($event)"
          (keydown)="onEmailRecipientKeydown($event)"
          (blur)="contacts.addEmailRecipient()" />
      </div>
      @if (contacts.emailRecipientError()) {
        <small data-email-recipient-error role="alert">{{ contacts.emailRecipientError() }}</small>
      }
    </label>

    <div data-email-field>
      <div data-email-label-row>
        <span>{{ t.templateLabel }}</span>
        <button
          type="button"
          data-email-link-btn
          [disabled]="!contacts.canSaveEmailTemplate()"
          [attr.aria-disabled]="!contacts.canSaveEmailTemplate()"
          (click)="toggleNewTemplateForm()">{{ t.saveAsTemplateBtn }}</button>
      </div>
      <select
        name="template"
        [attr.aria-label]="t.templateLabel"
        [value]="contacts.emailTemplateId()"
        (change)="onEmailTemplateChange($event)">
        <option value="" [selected]="!contacts.emailTemplateId()">{{ t.noTemplateOption }}</option>
        @for (tpl of contacts.allTemplates(); track tpl.id) {
          <option [value]="tpl.id" [selected]="tpl.id === contacts.emailTemplateId()">{{ tpl.label }}</option>
        }
      </select>
      @if (contacts.pendingEmailTemplateId()) {
        <div data-new-template-box data-template-replace-confirm role="alertdialog" [attr.aria-label]="t.replaceTemplatePrompt">
          <p data-template-prompt>{{ t.replaceTemplatePrompt }}</p>
          <div data-new-template-actions>
            <button type="button" data-email-inline-btn data-cancel-replace (click)="cancelEmailTemplateReplace()">{{ t.cancelTemplateBtn }}</button>
            <button type="button" data-email-inline-btn data-primary data-confirm-replace (click)="confirmEmailTemplateReplace()">{{ t.replaceTemplateConfirm }}</button>
          </div>
        </div>
      }
      @if (contacts.newTemplateOpen()) {
        <div data-new-template-box>
          @if (contacts.selectedEmailTemplate()) {
            <p data-template-prompt>{{ t.saveTemplatePrompt }}</p>
          }
          <input
            type="text"
            name="newTemplateName"
            [attr.aria-label]="t.newTemplateNamePlaceholder"
            [attr.placeholder]="t.newTemplateNamePlaceholder"
            [value]="contacts.newTemplateLabel()"
            (input)="onNewTemplateLabelInput($event)" />
          <div data-new-template-actions>
            <button type="button" data-email-inline-btn (click)="toggleNewTemplateForm()">{{ t.cancelTemplateBtn }}</button>
            @if (contacts.selectedEmailTemplate()) {
              <button type="button" data-email-inline-btn data-overwrite-template (click)="overwriteSelectedTemplate()">{{ t.overwriteTemplateConfirm }}</button>
            }
            <button
              type="button"
              data-email-inline-btn
              data-primary
              data-save-new-template
              [disabled]="!contacts.newTemplateLabel().trim()"
              (click)="saveNewTemplate()">{{ t.saveTemplateConfirm }}</button>
          </div>
        </div>
      }
    </div>

    <label data-email-field>
      <span>{{ t.subjectLabel }}</span>
      <input
        type="text"
        name="subject"
        [attr.aria-label]="t.subjectLabel"
        [attr.placeholder]="t.subjectPlaceholder"
        [value]="contacts.emailSubject()"
        (input)="onEmailSubjectInput($event)" />
    </label>

    <label data-email-field data-email-body>
      <span>{{ t.bodyLabel }}</span>
      <textarea
        name="body"
        rows="7"
        [attr.aria-label]="t.bodyLabel"
        [attr.placeholder]="t.bodyPlaceholder"
        [value]="contacts.emailBody()"
        (input)="onEmailBodyInput($event)"></textarea>
    </label>

    <div data-email-field>
      <div data-email-label-row>
        <span>{{ t.attachmentsLabel }}</span>
        <label data-email-file-trigger>
          <input
            type="file"
            multiple
            data-email-file-input
            (change)="onEmailAttachmentFilesSelected($event)" />
          <span>{{ t.addAttachmentBtn }}</span>
        </label>
      </div>
      @if (contacts.emailAttachments().length) {
        <ul data-email-attachment-list>
          @for (att of contacts.emailAttachments(); track att.id) {
            <li data-email-attachment-chip>
              <span>{{ att.fileName }} ({{ formatFileSize(att.sizeBytes) }})</span>
              <button
                type="button"
                [attr.aria-label]="t.removeAttachmentLabel + ' ' + att.fileName"
                (click)="contacts.removeEmailAttachment(att.id)">{{ t.removeChar }}</button>
            </li>
          }
        </ul>
      }
    </div>

    <label data-email-vcard for="email-attach-vcard">
      <span>{{ t.attachVCard }}</span>
      <button
        id="email-attach-vcard"
        type="button"
        role="switch"
        data-switch
        name="attachVCard"
        [attr.aria-checked]="contacts.emailAttachVCard()"
        [attr.aria-label]="t.attachVCard"
        (click)="contacts.toggleEmailVCard()"><i></i></button>
    </label>

    <div data-email-actions>
      @if (contacts.emailMailtoFallback(); as mailtoHref) {
        @if (contacts.emailEligibilityState().allowed) {
          <a data-mailto-link [href]="mailtoHref">{{ t.openInOutlook }}</a>
        }
      }
      <button
        type="submit"
        data-email-submit
        [disabled]="!contacts.emailEligibilityState().allowed || contacts.emailSending()"
        [attr.aria-disabled]="!contacts.emailEligibilityState().allowed || contacts.emailSending()"
        [attr.aria-busy]="contacts.emailSending() ? 'true' : null">
        @if (contacts.emailSending()) {
          <span data-button-spinner aria-hidden="true"></span>
        }
        <span>{{ contacts.emailSending() ? t.sendingEmail : t.sendEmail }}</span>
      </button>
    </div>
  </form>
} @else if (contacts.activePanel() === 'notes') {
  <div data-overlay-panel data-notes>
    <h3>{{ t.notesHeading }} · {{ contacts.active().name }}</h3>
    <textarea #overlayNoteInput [attr.placeholder]="t.notePlaceholder" [attr.aria-label]="t.notesHeading" rows="3"></textarea>
    <button ui-button variant="secondary" type="button" (click)="addOverlayNote(overlayNoteInput)">{{ t.addNoteBtn }}</button>
    @for (note of contacts.notesFor(contacts.activeId()); track note) {
      <p>{{ note }}</p>
    }
  </div>
} @else if (contacts.activePanel() === 'appointment') {
  <div data-overlay-panel data-appointment>
    <h3>{{ t.appointmentHeading }} · {{ contacts.active().name }}</h3>
    <p data-system>{{ contacts.active().detail }}</p>
    <label><span>{{ t.dateLabel }}</span><input #overlayApptDate type="date" [attr.aria-label]="t.dateLabel" /></label>
    <label><span>{{ t.timeLabel }}</span><input #overlayApptTime type="time" [attr.aria-label]="t.timeLabel" /></label>
    <button ui-button variant="primary" type="button" [disabled]="overlayScheduling()" (click)="scheduleOverlayAppointment(overlayApptDate, overlayApptTime)">{{ overlayScheduling() ? t.schedulingBtn : t.scheduleBtn }}</button>
    @if (overlayScheduledText()) { <p data-system>Scheduled for {{ overlayScheduledText() }}</p> }
    @if (overlayScheduleError()) { <p data-system role="alert">{{ overlayScheduleError() }}</p> }
  </div>
} @else if (contacts.activePanel() === 'timeline') {
  <div data-overlay-panel data-timeline>
    <h3>{{ t.timelineHeading }} · {{ contacts.active().name }}</h3>
    <ul>
      @for (entry of contacts.timelineFor(contacts.activeId()); track entry.id) {
        <li>
          <div><strong>{{ entry.title }}</strong><small>{{ entry.detail }}</small></div>
          <time>{{ entry.time }}</time>
        </li>
      } @empty {
        <li><small>{{ t.noTimelineEntries }}</small></li>
      }
    </ul>
  </div>
} @else if (remote && facade) {
  <ng-template #inboxTpl>
    <label data-search>
      <svg ui-icon name="search"></svg>
      <input type="search" [attr.placeholder]="t.search" [attr.aria-label]="t.searchLabel" [value]="facade.search()" (input)="onSearch($event)" />
    </label>
    @if ((facade.listStatus() === 'idle' || facade.listStatus() === 'loading') && facade.conversations().length === 0) {
      <span data-spinner role="status" [attr.aria-label]="t.loading">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span>
    } @else if (facade.listStatus() === 'error') {
      <p data-system>{{ facade.listError()?.message ?? t.listUnavailable }}</p>
    } @else if (facade.listStatus() === 'ready' && facade.conversations().length === 0) {
      <p data-system>{{ facade.search() ? t.noResults : t.empty }}</p>
    }
    <ul data-inbox [attr.aria-label]="t.conversations" (scroll)="onInboxScroll($event)">
      @for (conversation of facade.conversations(); track conversation.id) {
        <li>
          <button type="button" [attr.aria-current]="facade.selectedId() === conversation.id ? 'true' : null" (click)="openConversation(conversation.id)">
            <i>{{ conversation.initials }}</i>
            <span data-inbox-text>
              <span data-inbox-head><strong>{{ conversation.customerName }}</strong><time>{{ conversation.timeAgo }}</time></span>
              <span data-inbox-preview>{{ conversation.preview }}</span>
            </span>
            @if (conversation.unreadCount > 0) { <u [attr.aria-label]="t.unread"></u> }
          </button>
        </li>
      }
      @if (facade.conversationsLoadingMore()) {
        <li data-more><span data-spinner role="status" [attr.aria-label]="t.loading">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span></li>
      }
    </ul>
  </ng-template>
  <ng-template #threadTpl>
    <div data-thread-wrap #threadWrap (scroll)="onThreadScroll($event)">
      @if (facade.threadLoadingOlder()) {
        <span data-spinner role="status" [attr.aria-label]="t.loading">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span>
      }
      @if (facade.threadStatus() === 'loading') {
        <span data-spinner role="status" [attr.aria-label]="t.loading">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span>
      } @else if (facade.threadStatus() === 'error') {
        <p data-system>{{ facade.threadError()?.message ?? t.threadUnavailable }}</p>
      }
      <ul data-thread>
        @for (entry of threadEntries(); track entry.key) {
          @if (entry.kind === 'divider') {
            <li data-day role="separator">{{ entry.text }}</li>
          } @else {
            <li [attr.data-from]="fromAgent(entry.message) ? 'agent' : 'customer'" [attr.data-pending]="entry.message.pending ? '' : null" [attr.data-failed]="entry.message.failed ? '' : null" [attr.title]="entry.title || null">
              @let gif = gifOf(entry.message);
              @for (attachment of attachmentsOf(entry.message); track $index) {
                @if (attachment.image && attachment.url) {
                  @if (!gif) { <img data-attachment [src]="attachment.url" [alt]="attachment.name" /> }
                } @else { <small>{{ attachment.name }}</small> }
              }
              @if (gif) { <img data-attachment [src]="gif.url" [alt]="gif.label" /> }
              @else if (textOf(entry.message)) { <span>{{ textOf(entry.message) }}</span> }
              @if (entry.message.contactCard) {
                <span data-contact-card>
                  <i>{{ cardOwner().initials }}</i>
                  <span><strong>{{ cardOwner().name }}</strong><small>{{ t.contactCard }}</small></span>
                </span>
              }
            </li>
            @if (entry.message.failed && outboxErrorFor(entry.message.id); as reason) {
              <li data-delivery-error role="alert">
                <strong>{{ t.notDelivered }}</strong>
                <small>{{ reason }}</small>
                <span data-delivery-actions>
                  <button type="button" data-retry (click)="facade.retryOutbox(entry.message.id)">{{ t.retry }}</button>
                  <button type="button" data-delete (click)="facade.discardOutbox(entry.message.id)">{{ t.delete }}</button>
                </span>
              </li>
            }
          }
        }
      </ul>
    </div>
    <div data-bottom>
      <lib-messenger-composer [sendOnEnter]="true" [clearOnSend]="true" [suggestionCards]="true" [autoGrow]="true" [placeholder]="t.textMessage2" />
    </div>
  </ng-template>
  @if (expanded()) {
    <div data-split>
      <section data-split-list [attr.aria-label]="t.conversations">
        <ng-container [ngTemplateOutlet]="inboxTpl" />
      </section>
      <section data-split-thread>
        @if (facade.thread(); as open) {
          <header data-thread-header>
            <strong>{{ open.conversation.customerName }}</strong>
            <small>{{ open.conversation.customerPhone }}</small>
          </header>
          <ng-container [ngTemplateOutlet]="threadTpl" />
        } @else if (facade.selectedId() && facade.threadStatus() === 'loading') {
          <span data-spinner role="status" [attr.aria-label]="t.loading">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span>
        } @else {
          <p data-system>{{ t.select }}</p>
        }
      </section>
    </div>
  } @else if (remoteView() === 'list') {
    <ng-container [ngTemplateOutlet]="inboxTpl" />
  } @else {
    <button type="button" data-back (click)="backToList()">
      <svg ui-icon name="chevron-left"></svg>
      <span>{{ t.conversations }}</span>
    </button>
    <ul data-pins [attr.aria-label]="t.conversations" libDragScroll (scroll)="onPinsScroll($event)">
      @for (conversation of remotePins(); track conversation.id) {
        <li>
          <button type="button" [attr.aria-pressed]="facade.selectedId() === conversation.id" (click)="pinClick(conversation.id)">
            <i>{{ conversation.initials }}@if (conversation.unreadCount > 0) { <u [attr.aria-label]="t.unread"></u> }</i>
            <em>{{ conversation.customerName.split(' ')[0] }}</em>
          </button>
        </li>
      }
      @if (facade.conversationsLoadingMore()) {
        <li data-more><span data-spinner role="status" [attr.aria-label]="t.loading" [style.--spinner-margin]="'auto 1rem'">@for (spoke of spokes; track spoke) {<i [style.--spoke]="spoke"></i>}</span></li>
      }
    </ul>
    <ng-container [ngTemplateOutlet]="threadTpl" />
  }
  @if (fileDrag()) {
    <div data-drop-overlay aria-hidden="true">
      <div data-drop-card>
        <span data-drop-badge><svg ui-icon name="attach-drop"></svg></span>
        <strong>{{ t.dropTitle }}</strong>
        <small>{{ t.dropHint }}</small>
      </div>
    </div>
  }
} @else {
  <ul data-pins [attr.aria-label]="t.conversations" libDragScroll>
    @for (person of contacts.people; track person.id) {
      <li>
        <button type="button" [attr.aria-pressed]="contacts.activeId() === person.id" (click)="pinClick(person.id)">
          <i>{{ person.initials }}@if (contacts.isUnread(person)) { <u [attr.aria-label]="t.unread"></u> }</i>
          <em>{{ person.name.split(' ')[0] }}</em>
        </button>
      </li>
    }
  </ul>
  <div data-thread-wrap>
    <p data-system>{{ contacts.active().context }}</p>
    <ul data-thread>
      @for (message of contacts.thread(); track $index) {
        @if (message.gif) {
          <li [attr.data-from]="message.from" data-gif><span data-gif-tile aria-hidden="true">{{ message.gif.emoji }}</span><small>{{ t.gifLabel }} · {{ message.gif.label }}</small></li>
        } @else {
          <li [attr.data-from]="message.from">{{ message.text }}</li>
        }
      }
    </ul>
  </div>
  <div data-bottom>
    <div data-quick>
      @for (reply of quickReplies; track reply) { <button type="button" (click)="quick(reply)">{{ reply }}</button> }
    </div>
    @if (gifOpen()) {
      <div data-gif-panel role="dialog" [attr.aria-label]="t.gifLibrary">
        <input type="search" [attr.placeholder]="t.searchGifs" [attr.aria-label]="t.searchGifs" [value]="gifQuery()" (input)="onGifQuery($event)" />
        <ul>
          @for (gif of gifs(); track gif.id) {
            <li><button type="button" [attr.aria-label]="gif.label" (click)="pickGif(gif)"><span aria-hidden="true">{{ gif.emoji }}</span><small>{{ gif.label }}</small></button></li>
          } @empty { <li data-empty>{{ t.noGifs }}</li> }
        </ul>
      </div>
    }
    <form data-composer (submit)="$event.preventDefault(); send(textInput)">
      <label data-composer-field>
        <input #textInput type="text" [attr.placeholder]="t.textMessage2" [attr.aria-label]="t.textMessage" />
        <button type="button" data-gif-toggle [attr.aria-label]="t.gifLibrary" [attr.aria-expanded]="gifOpen()" (click)="toggleGifs()">
          <span data-gif-badge aria-hidden="true">{{ t.gifLabel }}</span>
        </button>
      </label>
      <button type="submit" [attr.aria-label]="t.send"><svg ui-icon name="arrow-up"></svg></button>
    </form>
  </div>
}
</aside>
